import {
  Badge,
  Box,
  Card,
  CardBody,
  Divider,
  Flex,
  HStack,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import Decimal from "decimal.js";
import { getCapitalActivityEventStateBadge } from "features/capital-activity/CapitalActivityUtils";
import { investorTransfereeUpsertSchema } from "features/investor-transfer/InvestorTransferSchemas";
import { InvestorTransferSetupFooter } from "features/investor-transfer/setup/InvestorTransferSetupFooter";
import { getTotalCommitmentAmountForTransferors } from "features/investor-transfer/utils";
import { useFundFamilyEventRetrieve } from "gen/endpoints/fund-family-event/fund-family-event";
import {
  useInvestorEntityList,
  useInvestorEntityListSuspense,
} from "gen/endpoints/investor-entity/investor-entity";
import {
  useInvestorTransfereeEntitiesListSuspense,
  useInvestorTransfereesUpsert,
  useInvestorTransferorsForInvestorTransferListSuspense,
  useInvestorTransferRetrieveSuspense,
} from "gen/endpoints/investor-transfer/investor-transfer";
import { InvestorTransfereeEntityUpsertData } from "gen/model/investorTransfereeEntityUpsertData";
import { InvestorTransfereeEntityUpsertListDataRequest } from "gen/model/investorTransfereeEntityUpsertListDataRequest";
import { InvestorTransferorReadInformationData } from "gen/model/investorTransferorReadInformationData";
import { useCallback, useMemo } from "react";
import { Control, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import Form from "shared/components/form/Form";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormSimpleSelect } from "shared/components/input/select/SimpleSelect";
import { FormSimpleMoneyInput } from "shared/components/input/text/SimpleMoneyInput";
import { SimpleNumberInput } from "shared/components/input/text/SimpleNumberInput";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { usePageWizard } from "shared/components/layout/PageWizard";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import {
  TableAddButton,
  TableRemoveButton,
} from "shared/components/table/TableRowActionButtons";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";
import {
  calculatePercentage,
  decimalOrZero,
  tryParseDecimal,
} from "shared/utils/DecimalUtils";
import { formatMoney } from "shared/utils/MoneyUtils";

export const InvestorTransferSetupTransfereesPage = () => {
  const { t } = useTranslation(["common", "investorTransfer"]);
  const { fundFamilyId, investorTransferId } = useRequiredStringParams();
  const { navigateNext } = usePageWizard();
  const queryClient = useQueryClient();

  const { data: investorTransferData } = useInvestorTransferRetrieveSuspense(
    fundFamilyId,
    investorTransferId,
  );

  const { data: fundFamilyEventData } = useFundFamilyEventRetrieve(
    fundFamilyId,
    investorTransferData.fund_family_event_id,
    {
      query: {
        enabled: !!investorTransferData.fund_family_event_id,
      },
    },
  );

  const {
    data: investorTransfereeEntities,
    queryKey: investorTransfereeEntitiesQueryKey,
  } = useInvestorTransfereeEntitiesListSuspense(
    fundFamilyId,
    investorTransferId,
  );

  const { data: investorTransferorDataList } =
    useInvestorTransferorsForInvestorTransferListSuspense(
      fundFamilyId,
      investorTransferId,
    );

  const totalTransferorCommitmentAmount =
    getTotalCommitmentAmountForTransferors(investorTransferorDataList);
  const {
    mutateAsync: upsertInvestorTransferees,
    isPending: upsertInvestorTransfereesLoading,
  } = useInvestorTransfereesUpsert();

  const form = useForm<InvestorTransfereeEntityUpsertListDataRequest>({
    resolver: (values, context, options) =>
      zodResolver(investorTransfereeUpsertSchema(context))(
        values,
        context,
        options,
      ),
    context: {
      commitmentAmount: decimalOrZero(totalTransferorCommitmentAmount),
    },
    defaultValues: {
      investor_transferee_entity_list: investorTransfereeEntities.length
        ? investorTransfereeEntities.map((transfereeEntity) => ({
            investor_entity_id: transfereeEntity.investor_entity_id,
            transfer_amount: transfereeEntity.transfer_amount,
          }))
        : // initialize with one empty row
          [{}],
    },
  });

  const {
    fields: transfereeEntities,
    append: addTransfereeEntity,
    remove: removeTransfereeEntity,
  } = useFieldArray({
    control: form.control,
    name: "investor_transferee_entity_list",
  });

  const onClickTransfereeEntityAdd = useCallback(() => {
    addTransfereeEntity({
      investor_entity_id: "",
      transfer_amount: "",
    });
  }, [addTransfereeEntity]);

  const onClickTransfereeEntityRemove = useCallback(
    (index: number) => {
      removeTransfereeEntity(index);
      if (transfereeEntities.length === 1) {
        onClickTransfereeEntityAdd();
      }
    },
    [removeTransfereeEntity, onClickTransfereeEntityAdd, transfereeEntities],
  );

  const isLoading = upsertInvestorTransfereesLoading;

  const transfereeEntityListField =
    (useWatch({
      control: form.control,
      name: "investor_transferee_entity_list",
    }) as InvestorTransfereeEntityUpsertData[] | undefined) ?? [];

  const filteredTransfereeEntityList =
    transfereeEntityListField.length > 0
      ? transfereeEntityListField.filter((field) => field.investor_entity_id)
      : [];

  const totalAmount = filteredTransfereeEntityList.reduce(
    (acc, curr) =>
      acc.add(tryParseDecimal(curr.transfer_amount) ?? new Decimal(0)),
    new Decimal(0),
  );

  const afterAmount = decimalOrZero(totalTransferorCommitmentAmount).sub(
    totalAmount,
  );

  return (
    <PageContent>
      <Form
        form={form}
        onSubmit={async (data) => {
          await upsertInvestorTransferees({
            fundFamilyId,
            investorTransferId,
            data,
          });
          await queryClient.invalidateQueries({
            queryKey: investorTransfereeEntitiesQueryKey,
          });
        }}
        onSuccess={navigateNext}
      >
        <PageHeader
          size="lg"
          workflowName={t("investorTransfer:setup.superHeading")}
          title={t("investorTransfer:setup.transferees.title")}
          badge={getCapitalActivityEventStateBadge(
            fundFamilyEventData?.state,
            t,
          )}
        />
        <PageBody>
          <Box>
            <Flex
              w="full"
              flex={1}
              gap={4}
              _last={{
                "& .chakra-form-control": {
                  marginBottom: 0,
                },
              }}
            >
              <Box flex={1}>
                <TableBox>
                  <TableBoxHeader>
                    <TableBoxTitle>
                      {t("investorTransfer:transferees")}
                    </TableBoxTitle>
                  </TableBoxHeader>
                  <TableBoxBody>
                    <TableContainer height="100%" overflowY="auto">
                      <Table size="tall">
                        <Thead>
                          <Tr>
                            <Th width={0} />
                            <Th width="300px">
                              {t("investorTransfer:transferee")}
                            </Th>
                            <Th width="350px">
                              {t("investorTransfer:transferAmountTitle")}
                            </Th>
                            <Th minWidth="150px" whiteSpace="normal">
                              {t("investorTransfer:transferPercentage")}
                            </Th>
                            <Th />
                          </Tr>
                        </Thead>
                        <Tbody>
                          {transfereeEntities.map((field, index: number) => (
                            <TransfereeRow
                              key={field.id}
                              formControl={form.control}
                              commitmentAmount={tryParseDecimal(
                                totalTransferorCommitmentAmount,
                              )}
                              transferorInvestorId={
                                investorTransferorDataList[0].investor_id
                              }
                              transfereeInvestorIds={filteredTransfereeEntityList.map(
                                (item) => item.investor_entity_id,
                              )}
                              index={index}
                              onClickTierAdd={onClickTransfereeEntityAdd}
                              onClickTierRemove={onClickTransfereeEntityRemove}
                            />
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </TableBoxBody>
                </TableBox>
              </Box>
              <Box width="330px" mb="24px">
                <Card variant="receipt">
                  <CardBody px="24px" py="8px">
                    <Box>
                      <Text
                        textStyle="overline"
                        color="gray.400"
                        flexGrow="1"
                        mb="16px"
                      >
                        {t("investorTransfer:setup.transferees.stat.title", {
                          name: investorTransferorDataList[0]
                            .investor_legal_name,
                        })}
                      </Text>
                      <StatGroup flexDirection="column">
                        <Stat size="md">
                          <StatNumber>
                            {formatMoney(totalTransferorCommitmentAmount)}
                          </StatNumber>
                          <StatLabel>
                            {t(
                              "investorTransfer:setup.transferees.stat.beforeTransfer",
                            )}
                          </StatLabel>
                        </Stat>
                        <Stat size="md">
                          <StatNumber>
                            {formatMoney(afterAmount.toString())}
                          </StatNumber>
                          <StatLabel>
                            {t(
                              "investorTransfer:setup.transferees.stat.afterTransfer",
                            )}
                          </StatLabel>
                        </Stat>
                      </StatGroup>
                    </Box>
                    {filteredTransfereeEntityList.length ? (
                      <Box>
                        <QueryBoundary>
                          <TransferDetails
                            fundFamilyId={fundFamilyId}
                            investorTransferorData={
                              investorTransferorDataList[0]
                            }
                            totalAmount={totalAmount}
                            transferees={filteredTransfereeEntityList}
                          />
                        </QueryBoundary>
                      </Box>
                    ) : null}
                  </CardBody>
                </Card>
              </Box>
            </Flex>
          </Box>
          <InvestorTransferSetupFooter
            fundFamilyId={fundFamilyId}
            allowBack={true}
            isLoading={isLoading}
          />
        </PageBody>
      </Form>
    </PageContent>
  );
};

const TransfereeRow: React.FC<{
  formControl: Control<InvestorTransfereeEntityUpsertListDataRequest>;
  commitmentAmount: Decimal | undefined;
  index: number;
  transferorInvestorId: string;
  transfereeInvestorIds: string[];
  onClickTierAdd: () => void;
  onClickTierRemove: (index: number) => void;
  isDisabled?: boolean;
}> = ({
  formControl,
  commitmentAmount,
  index,
  transferorInvestorId,
  transfereeInvestorIds,
  onClickTierAdd,
  onClickTierRemove,
  isDisabled,
}) => (
  <Tr>
    <Td px="16px !important">
      <TableAddButton onClick={onClickTierAdd} isDisabled={isDisabled} />
    </Td>
    <Td verticalAlign="top">
      <TransfereeInvestorSelect
        formControl={formControl}
        index={index}
        transferorInvestorId={transferorInvestorId}
        transfereeInvestorIds={transfereeInvestorIds}
      />
    </Td>
    <Td verticalAlign="top">
      <TransfereeRowValueField
        index={index}
        name="transfer_amount"
        isDisabled={isDisabled}
      />
    </Td>
    <Td verticalAlign="top">
      <HStack justifyContent="space-between">
        <TransfereeRowPercentageField
          formControl={formControl}
          commitmentAmount={commitmentAmount}
          transfereeAmountName={`investor_transferee_entity_list.${index}.transfer_amount`}
        />
      </HStack>
    </Td>
    <Td textAlign="right">
      <TableRemoveButton
        onClick={() => onClickTierRemove(index)}
        isDisabled={isDisabled}
      />
    </Td>
  </Tr>
);

const TransfereeInvestorSelect: React.FC<{
  formControl: Control<InvestorTransfereeEntityUpsertListDataRequest>;
  index: number;
  transferorInvestorId: string;
  transfereeInvestorIds: string[];
  isDisabled?: boolean;
}> = ({
  formControl,
  index,
  transferorInvestorId,
  transfereeInvestorIds,
  isDisabled,
}) => {
  const { t } = useTranslation(["investorTransfer"]);
  const { data: investors } = useInvestorEntityList({});

  const formSelectName = `investor_transferee_entity_list.${index}.investor_entity_id`;
  const currentTransfereeInvestorId = useWatch({
    control: formControl,
    name: `investor_transferee_entity_list.${index}.investor_entity_id`,
  });

  // find all investors that are not the transferor investor and are not already selected as a transferee (unless it is the current row)
  const nonTransferorInvestors = useMemo(
    () =>
      investors?.filter(
        (investor) =>
          investor.id &&
          investor.id != transferorInvestorId && // omit the transferor investor
          (!transfereeInvestorIds.includes(investor.id) ||
            investor.id == currentTransfereeInvestorId), // omit any selected transferees except the one selected in this row
      ),
    [
      investors,
      transferorInvestorId,
      transfereeInvestorIds,
      currentTransfereeInvestorId,
    ],
  );

  return (
    <FormSimpleSelect
      title={t("investorTransfer:transferee")}
      name={formSelectName}
      isDisabled={isDisabled}
    >
      {nonTransferorInvestors?.map(({ id, legal_name }) => (
        <SelectOption key={`investor_entity_${id}`} value={id!}>
          {legal_name}
        </SelectOption>
      ))}
    </FormSimpleSelect>
  );
};

const TransfereeRowValueField: React.FC<{
  index: number;
  name: string;
  isDisabled?: boolean;
}> = ({ index, name, isDisabled }) => {
  const { t } = useTranslation(["investorTransfer"]);
  return (
    <FormSimpleMoneyInput
      placeholder={t("investorTransfer:transferAmount")}
      name={`investor_transferee_entity_list.${index}.${name}`}
      isDisabled={isDisabled}
    />
  );
};

const TransfereeRowPercentageField: React.FC<{
  formControl: Control<InvestorTransfereeEntityUpsertListDataRequest>;
  commitmentAmount: Decimal | undefined;
  transfereeAmountName: string;
}> = ({ formControl, commitmentAmount, transfereeAmountName }) => {
  const { t } = useTranslation(["investorTransfer"]);
  const transfereeAmount = useWatch({
    control: formControl,
    name: transfereeAmountName as "investor_transferee_entity_list.0.transfer_amount",
  });
  const transfereeAmountDecimal = tryParseDecimal(transfereeAmount);
  return (
    <SimpleNumberInput
      title={t("investorTransfer:transferAmount")}
      value={calculatePercentage(commitmentAmount, transfereeAmountDecimal)}
      valueType="percent"
      isDisabled={true}
      isNotApplicable={!transfereeAmount}
      controlProps={{
        width: "unset",
        maxWidth: "150px",
      }}
    />
  );
};

const TransferDetails: React.FC<{
  fundFamilyId: string;
  transferees: InvestorTransfereeEntityUpsertData[];
  investorTransferorData: InvestorTransferorReadInformationData;
  totalAmount: Decimal;
}> = ({ fundFamilyId, transferees, investorTransferorData, totalAmount }) => {
  const { t } = useTranslation(["investorTransfer"]);

  const { data: investors } = useInvestorEntityListSuspense({});

  const { data: transferData } = useInvestorTransferRetrieveSuspense(
    fundFamilyId,
    investorTransferorData.investor_transfer_id,
  );

  return (
    <VStack alignItems="stretch" spacing="16px">
      <Divider borderColor="gray.200" />
      <HStack spacing="8px" alignItems="center">
        <Text textStyle="overline" color="gray.400">
          {t("investorTransfer:transferDetails")}
        </Text>
        <Badge>{formatIsoDateToDisplay(transferData.recorded_date)}</Badge>
      </HStack>
      <VStack alignItems="stretch" spacing="12px">
        {transferees.map((field) => (
          <HStack key={field.investor_entity_id} justifyContent="space-between">
            <Text textStyle="body">
              {
                investors.find((i) => i.id === field.investor_entity_id)
                  ?.legal_name
              }
            </Text>
            <Text textStyle="h5" lineHeight="20px">
              {formatMoney(field.transfer_amount || "0")}
            </Text>
          </HStack>
        ))}
      </VStack>
      <HStack justifyContent="space-between" alignItems="flex-end">
        <Text textStyle="body" fontWeight="600" color="primary.700">
          {t("investorTransfer:totalTransfer")}
        </Text>
        <Text textStyle="h4" fontWeight="600" color="primary.700">
          {formatMoney(totalAmount.toString())}
        </Text>
      </HStack>
    </VStack>
  );
};
